
import React, { useMemo, useState } from "react";
import VerticalBarChart from "examples/Charts/BarCharts/VerticalBarChart";
import PropTypes from "prop-types";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { Typography, CircularProgress } from "@mui/material";
import Card from "@mui/material/Card";
import dayjs from "dayjs";
import EntitiesPopup from "layouts/components/EntitiesPopup";

// ---------------------------------------------------------------------
// NOTE: Hooks are ALWAYS at top. No early returns before hooks.
// ---------------------------------------------------------------------

function AudienceEngagementBestDayChart({
  title,
  datasets = [],
  previousdatasets = [],
  isLoading = false,
  entitlements
}) {
  // Popup state (always defined → hook order stable)
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [popupDay, setPopupDay] = useState(null);

  // Handlers
  const handleBarClick = (dayName) => {
    setPopupDay(dayName);
    setIsPopupOpen(true);
  };

  const handlePopupClose = () => {
    setIsPopupOpen(false);
    setPopupDay(null);
  };

  // ---------------------------------------------------------------------
  // Safe datasets guard
  // ---------------------------------------------------------------------
  const safeDatasets = Array.isArray(datasets) ? datasets : [];

  // ---------------------------------------------------------------------
  // Memo: detect whether content exists
  // ---------------------------------------------------------------------
  const hasContent = useMemo(() => {
    return safeDatasets.some(
      (profile) =>
        profile?.data &&
        profile.data.some(
          (day) =>
            (day.posts && day.posts.length > 0) ||
            (day.videos && day.videos.length > 0)
        )
    );
  }, [safeDatasets]);

  // ---------------------------------------------------------------------
  // Aggregation logic (same as your original)
  // ---------------------------------------------------------------------
  const aggregateEngagementByDay = (profile) => {
    const engagementByDay = {
      Monday: 0,
      Tuesday: 0,
      Wednesday: 0,
      Thursday: 0,
      Friday: 0,
      Saturday: 0,
      Sunday: 0,
    };

    if (!profile?.data) return engagementByDay;

    profile.data.forEach((item) => {
      const date = new Date(item.date);
      const dayOfWeek = dayjs(date).format("dddd");

      let totalEngagement = 0;

      if (item.posts?.length > 0) {
        totalEngagement += item.posts.reduce((acc, post) => {
          return (
            acc +
            (post.postMetrics?.likes ||
              post.postMetrics?.like ||
              0) +
            (post.postMetrics?.comments || 0) +
            (post.postMetrics?.shares || 0) +
            (post.postMetrics?.love || 0) +
            (post.postMetrics?.wow || 0) +
            (post.postMetrics?.haha || 0) +
            (post.postMetrics?.sad || 0) +
            (post.postMetrics?.angry || 0)
          );
        }, 0);
      }

      if (item.videos?.length > 0) {
        totalEngagement += item.videos.reduce((acc, video) => {
          return (
            acc +
            (video.videoMetrics?.likeCount || 0) +
            (video.videoMetrics?.commentCount || 0)
          );
        }, 0);
      }

      if (engagementByDay.hasOwnProperty(dayOfWeek)) {
        engagementByDay[dayOfWeek] += totalEngagement;
      }
    });

    return engagementByDay;
  };

  // ---------------------------------------------------------------------
  // Chart data
  // ---------------------------------------------------------------------
  const chartData = {
    labels: hasContent
      ? ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
      : [],
    datasets: safeDatasets.map((profile) => {
      const engagement = aggregateEngagementByDay(profile);
      return {
        label: profile?.name || profile?.label || "Profile",
        data: Object.values(engagement),
        profileId: profile?.profileId,
        profilePictureUrl: profile?.profilePictureUrl || "",
        platform: profile?.platform || "Unknown",
        metricName: "Total Engagement",
      };
    }),
  };

  // ---------------------------------------------------------------------
  // Best day calculation
  // ---------------------------------------------------------------------
  // ---------------------------------------------------------------------
  // Best day calculation
  // ---------------------------------------------------------------------
  const bestDays = safeDatasets.map((profile) => {
    // 1. Current Best Day
    const engagement = aggregateEngagementByDay(profile);
    const totalEngagement = Object.values(engagement).reduce((acc, val) => acc + val, 0);
    const bestDay = totalEngagement > 0
      ? Object.keys(engagement).reduce((a, b) => engagement[a] > engagement[b] ? a : b)
      : "N/A";

    // 2. Previous Best Day
    let previousBestDay = "N/A";
    const previousProfile = Array.isArray(previousdatasets)
      ? previousdatasets.find(p => p.profileId === profile.profileId)
      : null;

    if (previousProfile) {
      const prevEngagement = aggregateEngagementByDay(previousProfile);
      const prevTotal = Object.values(prevEngagement).reduce((acc, val) => acc + val, 0);
      previousBestDay = prevTotal > 0
        ? Object.keys(prevEngagement).reduce((a, b) => prevEngagement[a] > prevEngagement[b] ? a : b)
        : "N/A";
    }

    return {
      profilePic: profile?.profilePictureUrl || "https://via.placeholder.com/150",
      name: profile?.name || profile?.label || "Profile",
      platform: profile?.platform || "Unknown Platform",
      metricName: "Best Engagement Day",
      metricValue: bestDay,
      previousMetricValue: previousBestDay,
      isQualitative: true // 🚩 Tell MetricCard not to show percentage change
    };
  });

  // ---------------------------------------------------------------------
  // Tooltip
  // ---------------------------------------------------------------------
  // ---------------------------------------------------------------------
  // Tooltip
  // ---------------------------------------------------------------------
  const tooltipContent = (
    <div style={{ maxWidth: "350px", textAlign: "left" }}>
      <Typography variant="body2" gutterBottom>
        <strong>Description:</strong>
      </Typography>
      <Typography variant="caption" gutterBottom display="block">
        This chart identifies which day of the week generates the most audience engagement.
        It aggregates interactions across all posts published on each weekday.
      </Typography>

      <Typography variant="body2" gutterBottom>
        <strong>How to read the chart:</strong>
      </Typography>
      <Typography variant="caption" gutterBottom display="block">
        - <strong>Tallest Bar</strong>: The day your audience is most active/responsive. <br />
        - <strong>Lower Bars</strong>: Days with less engagement. <br />
        - <strong>Uniform Bars</strong>: Consistent engagement throughout the week.
      </Typography>

      <Typography variant="body2" gutterBottom>
        <strong>Insights you can gain:</strong>
      </Typography>
      <Typography variant="caption" display="block">
        - Schedule key announcements on your best performing day. <br />
        - Understand your audience's weekly habits. <br />
        - Test posting on lower-performing days to see if you can boost activity.
      </Typography>
    </div>
  );

  // ---------------------------------------------------------------------
  // LOADING STATE (safe because hooks have run already)
  // ---------------------------------------------------------------------
  if (isLoading) {
    return (
      <Card>
        <MDBox
          p={3}
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="250px"
        >
          <CircularProgress size={32} thickness={4} color="accent" />
        </MDBox>
      </Card>
    );
  }

  // ---------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------
  return (
    <>
      <VerticalBarChart
        title={title}
        description={tooltipContent}
        height="19.125rem"
        chart={chartData}
        aggregate={true}
        metrics={bestDays}
        is_derived={true}
        onBarClick={handleBarClick}
        isEmpty={!hasContent} // ✅ Force empty state if no content, but keep cards via datasets
        rawData={safeDatasets}
        entitlements={entitlements} // ✅ Pass entitlements
        disableToggle={true} // ✅ Disable toggle for this chart
      />

      {isPopupOpen && (
        <EntitiesPopup
          isModalOpen={isPopupOpen}
          onClose={handlePopupClose}
          datasets={safeDatasets}
          filterDayOfWeek={popupDay}
        />
      )}
    </>
  );
}

// ---------------------------------------------------------------------
// PropTypes
// ---------------------------------------------------------------------
AudienceEngagementBestDayChart.propTypes = {
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  datasets: PropTypes.array,
  previousdatasets: PropTypes.array,
  isLoading: PropTypes.bool,
  entitlements: PropTypes.object,
};

export default AudienceEngagementBestDayChart;

// #################################################################################